import {
  Box,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core'
import { CustomIconContextMenu } from 'components/core/actions-menu-noborder/CustomIconContextMenu'
import { PropsWithChildren, ReactNode, useRef, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { ReactComponent as ActionContextIcon } from 'icons/action-context-icon.svg'
import { Waypoint } from 'react-waypoint'
import { Loader } from 'components/core/loader'

const StyledHeadTableCell = styled(TableCell)`
  && {
    color: ${props => props.theme.colors.accent};
    background-color: ${props => props.theme.colors.supplementary1};
    border-bottom-color: ${props => props.theme.colors.supplementary};
    border-width: 3;
  }
`

const StyledTableCell = styled(TableCell)`
  && {
    color: ${props => props.theme.colors.accent1};
    background-color: ${props => props.theme.colors.supplementary1};
    border-bottom-color: ${props => props.theme.colors.supplementary};
    border-width: 3;
    max-width: 11.25rem;
    overflow-wrap: break-word;
  }
`

const StyledLoader = styled(Box)`
  display: flex;
  color: ${props => props.theme.colors.accent1};
  font-size: 16px;
  border-width: 3;
  align-items: center;
  justify-content: center;
  width: 100%;
`

export interface ITableContainerColumn {
  /** Title of the column. Can also be a JSX element */
  name: string | ReactNode

  /** The name of the property in the row data */
  propName?: string
  /** Alignment for this column */
  align?: 'left' | 'center' | 'right'
  /**
   * A function used to intercept a column's data before it's added to the table cell.
   * You can return a JSX element or even truncate a long string. Very useful.
   * @param value The value about to be added to the table cell
   * @param row The row data
   */
  preprocessor?: (value: any, row: any, index: number) => any
  /** Properties that will be passed to the header table cell */
  cellProps?: TableCellProps
}

export interface ICustomTableProps {
  columns: ITableContainerColumn[]
  height: string
  data: any[]
  isReady?: boolean 

  checkMarksEnabled?: boolean
  /** When one clicks on a checkmark */
  onCheckMarkClicked?: (row: any, event: any) => void
  /**
   * Check if the checkbox should be checked
   * @param row row data
   * @returns whether the checkbox is ticked
   */
  checkedValidate?: (row: any) => boolean
  dropDownEnabled?: boolean | ((row: any) => boolean)
  /** Used to fill in the context menu with options */
  dropDownListItemsRender?: (row: any) => ReactNode | ReactNode[]
  /** A function that's triggered when you reach the bottom of the table. */
  onReachedBottom?: () => void
  /** Whether we should show the loader at the bottom of the screen */
  showLoader?: boolean
  /** Whether we should show the "No more items" at the bottom of the screen */
  showNoMoreText?: boolean
  /** The "No more items" at the bottom of the screen message */
  noMoreText?: ReactNode
}

export const CustomTable = ({
  columns,
  height,
  data,
  checkMarksEnabled,
  onCheckMarkClicked,
  dropDownEnabled,
  dropDownListItemsRender,
  onReachedBottom,
  showLoader,
  showNoMoreText,
  noMoreText,
  checkedValidate,
  isReady = true,
}: PropsWithChildren<ICustomTableProps>) => {
  const theme = useTheme()

  return (
    <TableContainer component={Paper} style={{ height }}>
      <Table aria-label="simple table" stickyHeader>
        <TableHead>
          <TableRow>
            {columns.map(({ align, name, cellProps }) => {
              return (
                <StyledHeadTableCell align={align || 'left'} {...cellProps}>
                  {name}
                </StyledHeadTableCell>
              )
            })}
          </TableRow>
        </TableHead>

        <TableBody>
          {data?.map((row, ix) => {
            const rowData = columns.map(({ propName, preprocessor, align }) => {
              // Check if the property is present in the data
              if (row?.hasOwnProperty(propName)) {
                const cellValue = preprocessor ? preprocessor(row[propName], row, ix) : row[propName]

                return <StyledTableCell align={align || 'left'}>{cellValue}</StyledTableCell>
              } else {
                // If prop isn't present but our preprocessor still returns something, then we continue on to rendering item
                if (preprocessor) {
                  const cellValue = preprocessor ? preprocessor('', row, ix) : null

                  if (!cellValue) return

                  return <StyledTableCell align={align || 'left'}>{cellValue}</StyledTableCell>
                }
              }
            })

            return (
              <TableRow>
                {checkMarksEnabled && (
                  <StyledTableCell>
                    <Checkbox
                      checked={checkedValidate(row)}
                      onClick={onCheckMarkClicked ? e => onCheckMarkClicked(row, e) : () => {}}
                    />
                  </StyledTableCell>
                )}

                {rowData}

                {(typeof dropDownEnabled === 'function' ? dropDownEnabled?.(row) : dropDownEnabled) && (
                  <StyledTableCell>
                    <CustomIconContextMenu
                      title=""
                      menuWidth="17em"
                      icon={<ActionContextIcon fill={theme.colors.accent1} />}
                    >
                      {dropDownListItemsRender && dropDownListItemsRender(row)}
                    </CustomIconContextMenu>
                  </StyledTableCell>
                )}
              </TableRow>
            )
          })}
        </TableBody>
        <Box width="100%" height="0.1vh">
          <Waypoint
            onEnter={() => {
              isReady && data && onReachedBottom && onReachedBottom?.()
            }}
          />
        </Box>
      </Table>
      {showLoader && (
        <StyledLoader>
          <Loader />
        </StyledLoader>
      )}
      {showNoMoreText && <Box>{noMoreText || 'No more items'}</Box>}
    </TableContainer>
  )
}
